/**
 * featureHomeModel.ts
 *
 * Pure model for the per-Feature home (`WorkflowLandingPage`): title, create
 * label, columns, stat cards, and the row view each job is turned into. No
 * React and no I/O.
 *
 * Stat cards and the list share one source of truth: every stat has a
 * predicate over `HomeRow`, and `countStat` / `filterRowsByStat` both use it,
 * so a card's count always equals the number of rows the list shows when that
 * card is selected (Requirement 2.4).
 *
 * Requirements: 2.3 (unit of work in title, stat cards, columns, create
 * button), 2.4 (stat cards agree with rows), 2.5 (columns), 11.11 (TR Grade).
 */

import { JobStatus, JobType, StepStatus, type Job } from '../../graphql/types';
import { getStepStatus } from '../../titleSearch/steps';
import { getDraftTitle } from '../../workspace/draftModel';
import { isTitleReviewFinalized, isTitleReviewReady } from '../../workspace/rowMode';
import { getTitleSearchStatusPill, hasOpenedTitleAnalysis } from '../../workspace/titleSearchStatus';
import type { PillTone } from '../ui/Pill';

// ─── Config ──────────────────────────────────────────────────────────────────

export type ColumnId =
  | 'name'
  | 'client'
  | 'loans'
  | 'borrower'
  | 'loanNumber'
  | 'guideline'
  | 'property'
  | 'order'
  | 'stage'
  | 'needsYou'
  | 'grade'
  | 'updated';

export interface ColumnDef {
  id: ColumnId;
  header: string;
}

export type StatId =
  | 'total'
  | 'needsYou'
  // TR
  | 'inProgress'
  | 'completed'
  // DD
  | 'fieldReview'
  | 'intake'
  | 'completedMonth'
  // BA
  | 'extracting'
  | 'ready'
  | 'closedWeek';

export interface StatDef {
  id: StatId;
  label: string;
  /** Caption under the count. Omit to use the unit of work ("jobs", "loan"). */
  hint?: string;
}

export interface FeatureHomeConfig {
  jobType: JobType;
  /** Page title: "Jobs", "Loans", "Title searches". */
  title: string;
  /** Title of the archived view. */
  archivedTitle: string;
  /** Primary button: "New job", "New loan", "New title search". */
  createLabel: string;
  /** Lower-case plural of the unit of work, for sentences. */
  unitPlural: string;
  /** Lower-case singular of the unit of work. */
  unitSingular: string;
  /** Feature accent colour (ui-design-system.md). */
  accent: string;
  columns: readonly ColumnDef[];
  stats: readonly StatDef[];
}

const col = (id: ColumnId, header: string): ColumnDef => ({ id, header });

const FEATURE_HOME_CONFIGS: Readonly<Record<JobType, FeatureHomeConfig>> = {
  [JobType.DD]: {
    jobType: JobType.DD,
    title: 'Jobs',
    archivedTitle: 'Archived jobs',
    createLabel: getDraftTitle(JobType.DD),
    unitPlural: 'jobs',
    unitSingular: 'job',
    accent: '#834DFB',
    columns: [
      col('name', 'Job'),
      col('client', 'Client'),
      col('loans', 'Loans'),
      col('stage', 'Stage'),
      col('needsYou', 'Needs you'),
      col('updated', 'Updated'),
    ],
    stats: [
      { id: 'needsYou', label: 'Waiting on you' },
      { id: 'fieldReview', label: 'In field review' },
      { id: 'intake', label: 'In intake' },
      { id: 'completedMonth', label: 'Completed this month' },
    ],
  },
  [JobType.BA]: {
    jobType: JobType.BA,
    title: 'Loans',
    archivedTitle: 'Archived loans',
    createLabel: getDraftTitle(JobType.BA),
    unitPlural: 'loans',
    unitSingular: 'loan',
    accent: '#A34DF5',
    columns: [
      col('borrower', 'Borrower'),
      col('loanNumber', 'Loan number'),
      col('guideline', 'Guideline'),
      col('stage', 'Stage'),
      col('needsYou', 'Needs you'),
      col('updated', 'Updated'),
    ],
    stats: [
      { id: 'needsYou', label: 'Waiting on you' },
      { id: 'extracting', label: 'Extracting' },
      { id: 'ready', label: 'Ready to complete' },
      { id: 'closedWeek', label: 'Closed this week' },
    ],
  },
  [JobType.TR]: {
    jobType: JobType.TR,
    title: 'Title searches',
    archivedTitle: 'Archived title searches',
    createLabel: getDraftTitle(JobType.TR),
    unitPlural: 'title searches',
    unitSingular: 'title search',
    accent: '#5A21C9',
    columns: [
      col('property', 'Property'),
      col('borrower', 'Borrower'),
      col('order', 'Order'),
      col('stage', 'Stage'),
      col('grade', 'Grade'),
      col('updated', 'Updated'),
    ],
    stats: [
      { id: 'total', label: 'Title searches', hint: 'In this list' },
      { id: 'needsYou', label: 'Needs you', hint: 'Review or next step' },
      { id: 'inProgress', label: 'Running', hint: 'Processing or analysing' },
      { id: 'completed', label: 'Analysis complete', hint: 'Graded searches' },
    ],
  },
};

export function getFeatureHomeConfig(jobType: JobType): FeatureHomeConfig {
  return FEATURE_HOME_CONFIGS[jobType] ?? FEATURE_HOME_CONFIGS[JobType.DD];
}

// ─── Rows ────────────────────────────────────────────────────────────────────

export type RowStageKey =
  | 'intake'
  | 'processing'
  | 'review'
  | 'analysis'
  /** DD/BA: analysis stage with every guideline check done. */
  | 'ready'
  | 'findings'
  | 'complete'
  | 'failed';

export type Grade = 'A' | 'B' | 'C' | 'D';

/** How DD/BA stage text is coloured: red when blocked, green when done. */
export type StageEmphasis = 'blocked' | 'done' | 'normal';

export interface HomeRow {
  job: Job;
  stageKey: RowStageKey;
  stageLabel: string;
  /** Pill tone, used by the TR Stage pill. DD/BA show coloured text instead. */
  stageTone: PillTone;
  stageEmphasis: StageEmphasis;
  /** What the user has to do next, or null when nothing is waiting on them. */
  needsYou: string | null;
  /**
   * How many things wait on the user, when the job carries that number
   * (documents left to verify). Null when unknown: the Needs you badge then
   * falls back to `needsYou` text.
   */
  needsYouCount: number | null;
  /** True for the row that is blocked (failed); its badge is tinted red. */
  blocked: boolean;
  /** Second line of the first column (DD: client; BA: "address · amount"). */
  primarySub: string | null;
  /** Grade of the latest completed run (TR), when known. */
  grade: Grade | null;
  /** Text for every column that is plain text. */
  cells: Readonly<Partial<Record<ColumnId, string>>>;
}

/** Shown where a value is missing. */
export const PLACEHOLDER = '—';

function norm(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

function text(value: string | null | undefined): string {
  const t = (value ?? '').trim();
  return t || PLACEHOLDER;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** The time of the last change we know about; the job has no `updatedAt`. */
export function getUpdatedAt(job: Pick<Job, 'createdAt' | 'startedAt' | 'completedAt'>): string {
  return job.completedAt || job.startedAt || job.createdAt;
}

/** "Just now", "5m ago", "3h ago", "2d ago", otherwise the local date. */
export function formatUpdated(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return PLACEHOLDER;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return PLACEHOLDER;
  const diffMs = Math.max(0, now.getTime() - date.getTime());
  const mins = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

function ddBaStageKey(job: Job): RowStageKey {
  switch (job.status) {
    case JobStatus.FAILED:
      return 'failed';
    case JobStatus.COMPLETED:
      return 'complete';
    case JobStatus.DOCUMENT_VERIFICATION:
      return 'review';
    case JobStatus.INVESTOR_GUIDELINE_REVIEW:
    case JobStatus.UNDERWRITING_GUIDELINE_REVIEW: {
      // Every guideline check done: nothing is left but completing the item.
      const total = job.totalGuidelineCount ?? 0;
      return total > 0 && (job.guidelineCheckCount ?? 0) >= total ? 'ready' : 'analysis';
    }
    case JobStatus.DOCUMENT_PROCESSING:
      return job.documentProcessingStatus === StepStatus.IN_PROGRESS ||
        job.documentProcessingStatus === StepStatus.COMPLETED
        ? 'processing'
        : 'intake';
    default:
      return 'intake';
  }
}

function trStageKey(job: Job): RowStageKey {
  if (job.status === JobStatus.FAILED) return 'failed';
  if (isTitleReviewFinalized(job)) {
    switch (norm(job.titleAnalysisStatus)) {
      case 'in_progress':
        return 'analysis';
      case 'completed':
        return 'complete';
      case 'failed':
        return 'failed';
      default:
        // Findings is completed once the reader has opened Title analysis.
        return hasOpenedTitleAnalysis(job) ? 'analysis' : 'findings';
    }
  }
  const docs = getStepStatus(job, 'document_processing');
  if (docs === 'failed' || docs === 'blocked') return 'failed';
  return isTitleReviewReady(job) ? 'review' : 'processing';
}

function ddBaStageTone(key: RowStageKey): PillTone {
  switch (key) {
    case 'failed':
      return 'danger';
    case 'ready':
    case 'complete':
      return 'success';
    case 'review':
      return 'warning';
    case 'analysis':
    case 'processing':
      return 'selected';
    default:
      return 'neutral';
  }
}

/**
 * Words for the DD/BA Stage column, as in boards 2a and 2b. Home wording is
 * kept here, not taken from the Top_Bar Status_Pill, which adds counts.
 */
function ddBaStageLabel(job: Job, key: RowStageKey): string {
  const isBa = job.jobType === JobType.BA;
  switch (key) {
    case 'intake':
      return 'Intake';
    case 'processing':
      return 'Extracting';
    case 'review':
      return 'Field review';
    case 'analysis':
      return isBa ? 'Guideline review' : 'Analysis';
    case 'ready':
      return 'Ready to complete';
    case 'complete':
      return 'Completed';
    case 'failed':
      return 'Failed';
    default:
      return PLACEHOLDER;
  }
}

/**
 * What waits on the user, as an action (`text`, also the tooltip) and, when the
 * job carries the number, a count. Only documents left to verify are counted
 * today: the job has no open-findings count, so Analysis keeps the action text.
 */
function ddBaNeedsYou(job: Job, key: RowStageKey): { text: string | null; count: number | null } {
  switch (key) {
    case 'intake':
      return { text: 'Add documents', count: null };
    case 'review': {
      const remaining = Math.max(
        (job.totalDocumentCount ?? 0) - (job.verifiedDocumentCount ?? 0),
        0
      );
      return remaining > 0
        ? { text: `${plural(remaining, 'document', 'documents')} to verify`, count: remaining }
        : { text: 'Continue to Analyze', count: null };
    }
    case 'analysis':
      return { text: 'Review findings', count: null };
    case 'failed':
      return { text: 'Check failure', count: null };
    default:
      return { text: null, count: null };
  }
}

function trNeedsYou(key: RowStageKey): string | null {
  switch (key) {
    case 'review':
      return 'Review fields';
    case 'findings':
      return 'Run title analysis';
    case 'failed':
      return 'Check failure';
    default:
      return null;
  }
}

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

/** BA second line: "118 Cedar Row, Austin TX · $412,000", from what the job has. */
function baPrimarySub(job: Job): string | null {
  const amount =
    typeof job.subjectLoanAmount === 'number' && Number.isFinite(job.subjectLoanAmount)
      ? usd.format(job.subjectLoanAmount)
      : '';
  const parts = [(job.propertyAddress ?? '').trim(), amount].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** Minimal storage shape, so the lookup below can run without a browser. */
export interface GuidelineStorage {
  getItem(key: string): string | null;
}

/**
 * Name of the guideline a loan is reviewed against. The job does not store it;
 * Analyze remembers the choice per job (`lastGuideline_<jobId>`), and a tenant
 * with a single guideline uses that one. Null when it cannot be resolved.
 */
export function resolveGuidelineName(
  jobId: string,
  names: ReadonlyMap<string, string>,
  storage: GuidelineStorage | null = defaultGuidelineStorage()
): string | null {
  let id: string | null = null;
  try {
    id = storage?.getItem(`lastGuideline_${jobId}`) ?? null;
  } catch {
    id = null;
  }
  if (id && names.has(id)) return names.get(id) ?? null;
  if (names.size === 1) return names.values().next().value ?? null;
  return null;
}

function defaultGuidelineStorage(): GuidelineStorage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

/**
 * Turn a job into the row the list shows. `grade` is the latest completed
 * run's grade for a TR search when it is known; it is ignored for DD/BA and
 * for a search whose analysis is not completed, so the Grade column only ever
 * shows the grade of a completed run (Requirement 11.11).
 */
export function buildHomeRow(
  job: Job,
  grade: Grade | null = null,
  guidelineName: string | null = null
): HomeRow {
  const isTr = job.jobType === JobType.TR;
  const isBa = job.jobType === JobType.BA;
  const stageKey = isTr ? trStageKey(job) : ddBaStageKey(job);
  const pill = isTr
    ? getTitleSearchStatusPill(job)
    : { label: ddBaStageLabel(job, stageKey), tone: ddBaStageTone(stageKey) };
  const need = isTr ? { text: trNeedsYou(stageKey), count: null } : ddBaNeedsYou(job, stageKey);
  const rowGrade = isTr && norm(job.titleAnalysisStatus) === 'completed' ? grade : null;

  const clientName = (job.clientName ?? '').trim();
  const primarySub = isTr ? null : isBa ? baPrimarySub(job) : clientName || null;
  const stageEmphasis: StageEmphasis =
    stageKey === 'failed' ? 'blocked' : stageKey === 'ready' || stageKey === 'complete' ? 'done' : 'normal';

  const cells: Partial<Record<ColumnId, string>> = {
    name: job.name?.trim() ? job.name.trim() : `Job ${job.jobId.slice(0, 8)}`,
    client: text(job.clientName),
    loans: plural(job.loanCount ?? 0, 'loan', 'loans'),
    // BA jobs are named for the loan, so the name is the best borrower label
    // until the borrower is stored on the job itself.
    borrower: text(job.borrowerName || (isTr ? '' : job.name)),
    loanNumber: text(job.loanNumber),
    guideline: text(guidelineName),
    property: text(job.propertyAddress || job.name),
    order: text(job.loanNumber),
    updated: formatUpdated(getUpdatedAt(job)),
  };

  return {
    job,
    stageKey,
    stageLabel: pill.label,
    stageTone: pill.tone,
    stageEmphasis,
    needsYou: need.text,
    needsYouCount: need.count,
    blocked: stageKey === 'failed',
    primarySub,
    grade: rowGrade,
    cells,
  };
}

// ─── Stats ───────────────────────────────────────────────────────────────────

const DAY_MS = 86_400_000;

/** True when the row's last change is in the same calendar month as `now`. */
function changedThisMonth(row: HomeRow, now: Date): boolean {
  const at = new Date(getUpdatedAt(row.job));
  return (
    !Number.isNaN(at.getTime()) &&
    at.getFullYear() === now.getFullYear() &&
    at.getMonth() === now.getMonth()
  );
}

/** True when the row's last change is within the last seven days. */
function changedThisWeek(row: HomeRow, now: Date): boolean {
  const at = new Date(getUpdatedAt(row.job)).getTime();
  return !Number.isNaN(at) && now.getTime() - at <= 7 * DAY_MS;
}

/**
 * Predicate behind each stat card. The list uses the same predicates when a
 * card is selected, so counts and rows cannot drift apart. Cards count rows
 * even where the prototype counts findings (Requirement 2.4).
 */
export function rowMatchesStat(row: HomeRow, stat: StatId, now: Date = new Date()): boolean {
  switch (stat) {
    case 'total':
      return true;
    case 'needsYou':
      return row.needsYou !== null;
    case 'inProgress':
      return (
        row.stageKey === 'processing' ||
        (row.job.jobType === JobType.TR && row.stageKey === 'analysis')
      );
    case 'completed':
      return row.stageKey === 'complete';
    case 'fieldReview':
      return row.stageKey === 'review';
    case 'intake':
      return row.stageKey === 'intake';
    case 'extracting':
      return row.stageKey === 'processing';
    case 'ready':
      return row.stageKey === 'ready';
    case 'completedMonth':
      return row.stageKey === 'complete' && changedThisMonth(row, now);
    case 'closedWeek':
      return row.stageKey === 'complete' && changedThisWeek(row, now);
  }
}

export function filterRowsByStat(
  rows: readonly HomeRow[],
  stat: StatId,
  now: Date = new Date()
): HomeRow[] {
  return rows.filter((row) => rowMatchesStat(row, stat, now));
}

export function countStat(rows: readonly HomeRow[], stat: StatId, now: Date = new Date()): number {
  return filterRowsByStat(rows, stat, now).length;
}

/** Counts for every stat card of a Feature, in card order. */
export function computeStats(
  rows: readonly HomeRow[],
  config: FeatureHomeConfig,
  now: Date = new Date()
): Array<StatDef & { count: number; hint: string }> {
  return config.stats.map((stat) => {
    const count = countStat(rows, stat.id, now);
    return {
      ...stat,
      count,
      hint: stat.hint ?? (count === 1 ? config.unitSingular : config.unitPlural),
    };
  });
}

// --- Header copy -------------------------------------------------------------

/** Rows that are still being worked: not finished and not ready to complete. */
function isActive(row: HomeRow): boolean {
  return row.stageKey !== 'complete' && row.stageKey !== 'ready';
}

/**
 * Text of the count pill next to the title: "3 active" (DD), "5 in progress"
 * (BA). Title Search keeps the plain row count.
 */
export function getCountPill(rows: readonly HomeRow[], config: FeatureHomeConfig): string {
  const active = rows.filter(isActive).length;
  switch (config.jobType) {
    case JobType.DD:
      return `${active} active`;
    case JobType.BA:
      return `${active} in progress`;
    default:
      return String(rows.length);
  }
}

/**
 * The one-line summary under the title. DD and BA say what waits on the user
 * (naming the item when there is one); Title Search keeps "n of m need you".
 * `partial` appends a note that the counts cover only the rows loaded so far.
 */
export function getSummary(
  rows: readonly HomeRow[],
  config: FeatureHomeConfig,
  partial = false
): string {
  const waiting = rows.filter((row) => row.needsYou !== null);
  const note = partial ? ' Counts cover the rows loaded so far.' : '';
  if (config.jobType === JobType.TR) {
    return `${waiting.length} of ${rows.length} ${rows.length === 1 ? config.unitSingular : config.unitPlural} ${waiting.length === 1 ? 'needs' : 'need'} you.${note}`;
  }
  if (waiting.length === 0) {
    return `Nothing is waiting on you.${note}`;
  }
  if (waiting.length === 1) {
    const only = waiting[0];
    const label =
      (config.jobType === JobType.BA ? only.cells.borrower : only.cells.name) ?? config.unitSingular;
    return `${label} is waiting on you: ${only.needsYou?.toLowerCase()}.${note}`;
  }
  return `${waiting.length} ${config.unitPlural} are waiting on you.${note}`;
}

// ─── Grade pill ──────────────────────────────────────────────────────────────

/**
 * Grade pill classes from the status table: A and B are green, C and D use the
 * Grade C/D card colours (orange), anything else is neutral.
 */
export function gradeTone(grade: Grade): 'success' | 'attention' {
  return grade === 'A' || grade === 'B' ? 'success' : 'attention';
}

export const GRADE_ATTENTION_CLASSES = 'border border-[#fdba74] bg-[#fff7ed] text-[#c2410c]';

export function isGrade(value: unknown): value is Grade {
  return value === 'A' || value === 'B' || value === 'C' || value === 'D';
}
